import { Country, State } from "country-state-city";
import api, { isSuccessResponse } from "lib/api";
import { API_ENDPOINTS } from "lib/apiUrl";
import { SetAuthUserData } from "store/reducer";

function getReactSelectOptionClassName({ isFocused, isSelected }) {
  const base = "!px-3 !py-2 !cursor-pointer";
  if (isSelected) return `${base} !bg-accent !text-accent-foreground`;
  if (isFocused) return `${base} !bg-accent/40`;
  return `${base} !bg-transparent`;
}

/** react-select class names aligned with Fortify inputs */
export const reactSelectClassNames = {
  control: ({ isFocused }) =>
    [
      "!min-h-10 !rounded-md !border !bg-background !text-sm",
      isFocused ? "!border-ring !ring-1 !ring-ring" : "!border-input",
    ].join(" "),
  valueContainer: () => "!px-3",
  placeholder: () => "!text-muted-foreground",
  menu: () =>
    "!mt-1 !rounded-md !border !border-input !bg-popover !text-sm !shadow-md !z-50",
  option: getReactSelectOptionClassName,
  indicatorSeparator: () => "!hidden",
};

function findCountryByIso(iso) {
  const code = String(iso ?? "").trim().toUpperCase();
  if (!code) return null;
  return (
    Country.getAllCountries().find((c) => c.isoCode.toUpperCase() === code) ??
    null
  );
}

function findCountryByName(name) {
  const raw = String(name ?? "").trim();
  if (!raw) return null;
  return (
    Country.getAllCountries().find(
      (c) =>
        c.name === raw ||
        c.name.toLowerCase() === raw.toLowerCase() ||
        c.isoCode.toUpperCase() === raw.toUpperCase()
    ) ?? null
  );
}

function findState(countryIso, provinceValue) {
  const raw = String(provinceValue ?? "").trim();
  if (!countryIso || !raw) return null;
  const states = State.getStatesOfCountry(countryIso);
  return (
    states.find(
      (s) =>
        s.isoCode.toUpperCase() === raw.toUpperCase() ||
        s.name === raw ||
        s.name.toLowerCase() === raw.toLowerCase()
    ) ?? null
  );
}

/** API stores ISO codes; react-select uses country/state names. */
export function mapAddressApiToForm(addr = {}) {
  const countryRecord = findCountryByIso(addr.country) ?? findCountryByName(addr.country);
  const countryName = countryRecord?.name ?? String(addr.country ?? "").trim();
  const countryIso = countryRecord?.isoCode ?? "";
  const stateRecord = findState(countryIso, addr.province);
  const provinceName = stateRecord?.name ?? String(addr.province ?? "").trim();

  return {
    line1: String(addr.line1 ?? "").trim(),
    city: String(addr.city ?? "").trim(),
    province: provinceName,
    postalCode: String(addr.postalCode ?? "")
      .replace(/\s+/g, "")
      .toUpperCase(),
    country: countryName,
  };
}

/** Persist address using API ISO codes for country and province. */
export function mapAddressFormToApi(formAddr) {
  const countryRecord = findCountryByName(formAddr.country);
  const countryIso = countryRecord?.isoCode ?? String(formAddr.country ?? "").trim();
  const stateRecord = findState(countryIso, formAddr.province);

  return {
    line1: String(formAddr.line1 ?? "").trim(),
    city: String(formAddr.city ?? "").trim(),
    province: stateRecord?.isoCode ?? String(formAddr.province ?? "").trim(),
    postalCode: String(formAddr.postalCode ?? "").trim(),
    country: countryIso,
  };
}

export function getProfileAddressFromUser(user, corporate) {
  const profile = corporate ? user?.corporateProfile : user?.individualProfile;
  return profile?.[corporate ? "registeredAddress" : "residentialAddress"] || {};
}

export function padBeneficialOwners(raw) {
  if (!Array.isArray(raw)) return ["", "", "", "", ""];
  const mapped = raw.map((o) => String(o ?? "").trim());
  return [...mapped, "", "", "", "", ""].slice(0, 5);
}

export function mapAuthUserToProfileForm(user, corporate) {
  const ind = user?.individualProfile || {};
  const corp = user?.corporateProfile || {};
  const address = mapAddressApiToForm(getProfileAddressFromUser(user, corporate));

  return {
    phone: String(user?.phone ?? "").replace(/\D/g, ""),
    firstName: String(ind.firstName ?? corp.firstName ?? "").trim(),
    lastName: String(ind.lastName ?? corp.lastName ?? "").trim(),
    companyName: String(corp.companyName ?? "").trim(),
    ...address,
    isOver19Confirmed: Boolean(
      ind.isOver19Confirmed ?? corp.isOver19Confirmed
    ),
    beneficialOwners: padBeneficialOwners(corp.beneficialOwners),
  };
}

/** Ensures react-select can display a saved value not in the library list. */
export function withSelectOptionFallback(options, value) {
  const v = String(value ?? "").trim();
  if (!v) return options;
  if (options.some((o) => o.value === v)) return options;
  return [{ value: v, label: v }, ...options];
}

export async function refreshAuthUser(dispatch) {
  const meResp = await api.get(API_ENDPOINTS.me);
  if (!isSuccessResponse(meResp)) {
    const msg =
      (meResp && typeof meResp === "object" && (meResp.message || meResp.error)) ||
      "Could not refresh your profile.";
    throw new Error(msg);
  }
  const root = meResp?.data ?? meResp ?? {};
  const nextUser =
    (root.user && typeof root.user === "object" ? root.user : null) ||
    (root.email || root.individualProfile || root.corporateProfile ? root : null);
  if (nextUser) {
    dispatch(SetAuthUserData({ user: nextUser }));
  }
}
